import { SelectedMetric } from 'containers/Esrs';
import {
  useOptOutMetricMutation,
  GetMetricAnswersDocument_,
  GetOptedOutMetricsDocument_,
} from 'models';
import { Menu } from 'Molecules/Menu';
import { useParams } from 'react-router-dom';
import { useToast } from 'utils/hooks';
import { MaybeHasChild, MetricsTableData, useEsrsAnswer } from '../MetricAnswers.hooks';
import { useMemo } from 'react';

export const MetricMenu = ({
  metric,
  onOpen,
  setSelectedMetric,
  companyReportingUnit,
}: {
  metric: MetricsTableData['metric'];
  onOpen: () => void;
  setSelectedMetric: (param: SelectedMetric) => void;
  companyReportingUnit?: string;
}) => {
  const { reportingUnitId = companyReportingUnit, esrsAssessmentId = '' } = useParams();
  const [optOut] = useOptOutMetricMutation();
  const toast = useToast();
  const { answer } = useEsrsAnswer(metric?.reference, companyReportingUnit);

  const allMetricRefs = useMemo(() => {
    const refs = [];
    const recurseChildren = (child: MaybeHasChild): void => {
      if (child.childrenMetrics?.length) {
        refs.push(child.reference);
        return child.childrenMetrics.forEach(
          (m) => m.childMetric && recurseChildren(m.childMetric)
        );
      }
      refs.push(child.reference);
      return;
    };
    refs.push(metric.reference);
    if (metric.childrenMetrics?.length)
      metric.childrenMetrics.forEach((m) => m.childMetric && recurseChildren(m.childMetric));
    return refs;
  }, [metric]);

  const startReporting = () => {
    optOut({
      variables: {
        objects: allMetricRefs.map((ref) => ({
          metricRef: ref,
          reportingUnitId,
          assessmentId: esrsAssessmentId,
          hasOptedOut: false,
          optOutReason: '',
        })),
      },
      refetchQueries: [GetMetricAnswersDocument_, GetOptedOutMetricsDocument_],
    }).catch(() => {
      toast({ text: 'Failed to start reporting', variant: 'danger' });
    });
  };

  return (
    <Menu
      buttonVariant="secondary"
      sections={[
        {
          actions: [
            answer?.hasOptedOut
              ? {
                  id: 'start-reporting',
                  title: 'Start Reporting',
                  description:
                    'You are opted out from reporting on this metric. You can start reporting again',
                  onClick: startReporting,
                }
              : {
                  id: 'opt-out',
                  title: 'Mark as irrelevant',
                  onClick: () => {
                    setSelectedMetric(metric as SelectedMetric);
                    onOpen();
                  },
                },
          ],
        },
      ]}
      size={answer?.hasOptedOut ? 'md' : 'sm'}
    />
  );
};
